import { Skeleton } from '@dixa/lib-shared-habla';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import {
  DashboardSLAPolicy,
  DashboardSLAPolicyMetric,
  ResponseTimeMetric,
} from 'lib-dashboard-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import { DashboardSLAResponseTimeMetric } from 'app/views/layout/views/dashboard/components/metric/sla/slaResponseTimeMetric';
import {
  QueueDashboardMetric,
  QueueDashboardMetricProps,
} from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';

import { useQueueDashboardQueueContext } from '../../../../../queue/queueDashboardQueue.context';

type QueueDashboardSLAResponseTimeMetricProps = {
  /** The response time metric. */
  metric?: ResponseTimeMetric;
  /** The SLA policy which applies to this metric. */
  slaPolicy: DashboardSLAPolicy;
} & PropsWithClassName;

export const QueueDashboardSLAResponseTimeMetric = memo(
  function QueueDashboardSLAResponseTimeMetric({
    className,
    metric,
    slaPolicy,
  }: QueueDashboardSLAResponseTimeMetricProps): JSX.Element {
    const { isSkeleton } = useQueueDashboardQueueContext();

    const slaPolicyMetric = useMemo(():
      | DashboardSLAPolicyMetric
      | undefined => {
      const match = slaPolicy.metrics.find(
        ({ metric }) => metric === 'response_time',
      );

      return match
        ? {
            ...match,
            achieved_percentage: metric?.sla_percentage ?? undefined,
            evaluation_status: metric?.threshold_breached
              ? 'Breached'
              : 'Complied',
          }
        : undefined;
    }, [metric, slaPolicy]);

    const thresholdAlertTooltip =
      useMemo((): QueueDashboardMetricProps['alertTooltip'] => {
        if (
          slaPolicyMetric?.evaluation_status !== 'Breached' ||
          !slaPolicyMetric.achieved_percentage
        ) {
          return null;
        }

        return {
          head: 'Service-level alert',
          content: (
            <>
              This queue is below the goal of{' '}
              <b>{slaPolicyMetric.percentage}%</b> conversations answered within{' '}
              <b>
                {formatInstrumentNumber(slaPolicyMetric.target, 'duration')}
              </b>
              .
            </>
          ),
        };
      }, [slaPolicyMetric]);

    return (
      <QueueDashboardMetric
        className={className}
        metricType="response_time"
        title="Response time SLA"
        isSkeleton={isSkeleton}
        alertTooltip={thresholdAlertTooltip}
      >
        {isSkeleton ? (
          <Skeleton height="24px" width="72px" />
        ) : (
          <DashboardSLAResponseTimeMetric
            responseTime={metric?.average_response_time}
            slaPolicyId={slaPolicy.id}
            slaPolicyMetric={slaPolicyMetric}
          />
        )}
      </QueueDashboardMetric>
    );
  },
);
